import { useState } from 'react';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import {
  Bell, Wifi, WifiOff, FileUp, CheckCircle, XCircle,
  FileCheck, ArrowRight, Trash2, AlertTriangle
} from 'lucide-react';

const EVENT_CONFIG = {
  invoice_uploaded: { icon: FileUp, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Invoice Uploaded' },
  invoice_confirmed: { icon: FileCheck, color: 'text-green-500', bg: 'bg-green-50', label: 'Extraction Confirmed' },
  invoice_rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Invoice Rejected' },
  approval_granted: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', label: 'Approval Granted' },
  approval_rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Approval Rejected' },
  status_changed: { icon: ArrowRight, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Status Changed' },
  extraction_complete: { icon: FileCheck, color: 'text-green-600', bg: 'bg-green-50', label: 'AI Extraction Complete' },
  trade_id_rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Trade ID Rejected' },
  submitted_for_approval: { icon: CheckCircle, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Submitted for Approval' },
  flagged_for_review: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Flagged for Review' },
  sla_escalated: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'SLA Breach Escalated' },
};

function timeAgo(timestamp) {
  if (!timestamp) return '';
  const diff = Date.now() - new Date(timestamp).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationPanel() {
  const { notifications, connected, unreadCount, clearUnread, clearAll } = useWebSocket();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) clearUnread();
  };

  const handleClick = (n) => {
    if (n.invoice_id) {
      navigate(`/invoices/${n.invoice_id}`);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="notification-bell"
          className="relative rounded-sm h-8 w-8 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#E11D48] text-white text-[9px] font-bold flex items-center justify-center animate-in zoom-in-50 duration-200"
              data-testid="unread-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="right"
        sideOffset={8}
        className="w-80 p-0 rounded-sm border border-slate-200 shadow-lg"
        data-testid="notification-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-slate-800 font-heading">Notifications</h3>
            <div className="flex items-center gap-1">
              {connected ? (
                <Wifi className="h-3 w-3 text-green-500" />
              ) : (
                <WifiOff className="h-3 w-3 text-red-400" />
              )}
              <span className={`text-[9px] uppercase tracking-wider font-semibold ${connected ? 'text-green-600' : 'text-red-400'}`}>
                {connected ? 'Live' : 'Offline'}
              </span>
            </div>
          </div>
          {notifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              data-testid="clear-notifications"
              className="text-[10px] text-slate-400 hover:text-slate-600 h-6 px-2 rounded-sm"
            >
              <Trash2 className="h-3 w-3 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="max-h-96">
          {notifications.length === 0 ? (
            <div className="py-12 text-center">
              <Bell className="h-8 w-8 mx-auto text-slate-200 stroke-[1px]" />
              <p className="mt-3 text-xs text-slate-400">No notifications yet</p>
              <p className="text-[10px] text-slate-300 mt-1">Pipeline events will appear here in real time</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {notifications.map((n, i) => {
                const config = EVENT_CONFIG[n.type] || { icon: Bell, color: 'text-slate-400', bg: 'bg-slate-50', label: n.type };
                const Icon = config.icon;
                return (
                  <button
                    key={`${n.timestamp}-${i}`}
                    onClick={() => handleClick(n)}
                    data-testid={`notification-item-${i}`}
                    className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50/80 transition-colors"
                  >
                    <div className={`w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                      <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700">{config.label}</p>
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">
                        <span className="font-mono">{n.invoice_number}</span>
                        {n.vendor_name && <span> &middot; {n.vendor_name}</span>}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {n.user_name && (
                          <span className="text-[10px] text-slate-400">by {n.user_name}</span>
                        )}
                        {n.tier && (
                          <Badge variant="outline" className="rounded-sm text-[8px] uppercase tracking-wider px-1.5 py-0">{n.tier}</Badge>
                        )}
                        <span className="text-[10px] text-slate-300 ml-auto">{timeAgo(n.timestamp)}</span>
                      </div>
                    </div>
                    <ArrowRight className="h-3 w-3 text-slate-200 mt-1 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
